import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  Input, 
  Button, 
  Form, 
  Select, 
  Typography, 
  Space, 
  Tag, 
  Card, 
  Row, 
  Col,
  Tooltip,
  message,
  AutoComplete
} from 'antd';
import { 
  SaveOutlined, 
  CloseOutlined, 
  TagOutlined, 
  PlusOutlined, 
  InfoCircleOutlined, 
  BranchesOutlined, 
  FolderOutlined,
  PictureOutlined
} from '@ant-design/icons';
import DB from '../services/dbService';
import ImageUploader from './ImageUploader';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

// 提示词编辑器组件，用于创建新提示词、编辑现有提示词或添加新版本
function PromptEditor({ mode, prompt, onSave, onCancel, folders = [], selectedFolder = null, tags: allTags = [], onAddNewTag }) {
  console.log('PromptEditor mode:', mode, 'prompt:', prompt);

  // 初始化表单状态
  const [title, setTitle] = useState(mode === 'create' ? prompt?.title || '新提示词' : prompt?.title || '');
  const [content, setContent] = useState(
    mode === 'add-version' 
      ? prompt?.versions[prompt.currentVersionIndex].content || '' 
      : mode === 'edit' 
        ? prompt?.versions[prompt.currentVersionIndex].content || '' 
        : ''
  );
  const [description, setDescription] = useState('');
  const [introduction, setIntroduction] = useState(mode === 'create' ? prompt?.introduction || '' : prompt?.introduction || '');
  const [versionNumber, setVersionNumber] = useState(
    mode === 'add-version' 
      ? (prompt?.versions.length + 1).toString() 
      : mode === 'create' 
        ? '1.0' 
        : prompt?.versions[prompt.currentVersionIndex].versionNumber || '1.0'
  );
  const [tagInput, setTagInput] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState(
    mode === 'create' ? prompt?.tags || [] : prompt?.tags || []
  );
  const [folderId, setFolderId] = useState(
    mode === 'create' 
      ? selectedFolder 
      : mode === 'edit' 
        ? prompt?.folderId 
        : null
  );
  const [tagSuggestions, setTagSuggestions] = useState([]);
  // 初始化结果图片列表
  const [resultImages, setResultImages] = useState(() => {
    if (mode === 'edit' && prompt?.versions && prompt.versions[prompt.currentVersionIndex]) {
      // 在编辑模式下，使用当前版本的图片
      return prompt.versions[prompt.currentVersionIndex].resultImages || [];
    }
    return [];
  });

  // 根据模式设置页面标题
  const getTitle = () => {
    switch (mode) {
      case 'create':
        return '创建新提示词';
      case 'edit':
        return '编辑提示词';
      case 'add-version':
        return '添加新版本';
      default:
        return '';
    }
  };

  // 当标签输入变化时，更新建议列表
  useEffect(() => {
    if (tagInput.trim()) {
      // 过滤出匹配的标签，但排除已选择的标签
      const matchedTags = allTags
        .filter(tag => 
          tag.name.toLowerCase().includes(tagInput.trim().toLowerCase()) && 
          !selectedTagIds.includes(tag.id)
        )
        .map(tag => ({
          value: tag.name,
          label: (
            <div className="flex items-center justify-between">
              <span>{tag.name}</span>
              <span className="text-gray-400 text-xs">已有标签</span>
            </div>
          ),
          tag: tag
        }));
      setTagSuggestions(matchedTags);
    } else {
      setTagSuggestions([]);
    }
  }, [tagInput, allTags, selectedTagIds]);

  // 处理标签选择
  const handleTagSelect = (value, option) => {
    if (option && option.tag) {
      // 选择了已有标签
      if (!selectedTagIds.includes(option.tag.id)) {
        setSelectedTagIds([...selectedTagIds, option.tag.id]);
        message.success(`已添加标签: ${option.tag.name}`);
      } else {
        message.info(`标签已存在: ${option.tag.name}`);
      }
    } else {
      // 创建新标签
      addTag(value);
    }
    setTagInput('');
  };

  // 添加标签
  const addTag = (value = null) => {
    const tagName = value || tagInput.trim();
    if (tagName) {
      // 检查是否已存在此标签
      const existingTag = allTags.find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
      
      if (existingTag) {
        // 如果标签已存在，添加标签ID
        if (!selectedTagIds.includes(existingTag.id)) {
          setSelectedTagIds([...selectedTagIds, existingTag.id]);
          message.success(`已添加标签: ${existingTag.name}`);
        } else {
          message.info(`标签已存在: ${existingTag.name}`);
        }
      } else {
        // 创建新标签并添加
        const newTagId = uuidv4();
        // 调用App组件中的方法创建新标签
        const newTag = { id: newTagId, name: tagName };
        // 这里需要一个回调函数来添加新标签到全局标签列表
        if (onAddNewTag) {
          onAddNewTag(newTag);
          // 将新标签ID添加到已选标签中
          setSelectedTagIds([...selectedTagIds, newTagId]);
          message.success(`已创建并添加新标签: ${newTag.name}`);
        }
      }
      setTagInput('');
    }
  };

  // 删除标签
  const removeTag = (tagId) => {
    setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
  };

  // 处理标签输入框按键事件（按回车添加标签）
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // 表单提交处理
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    console.log('表单提交数据:', { 
      mode, 
      title, 
      introduction, 
      content, 
      tags: selectedTagIds, 
      folderId, 
      versionNumber 
    });
    
    // 表单验证
    if (!title.trim() && (mode === 'create' || mode === 'edit')) {
      message.error('请输入提示词标题');
      return;
    }
    
    if (!content.trim()) {
      message.error('请输入提示词内容');
      return;
    }
    
    // 根据模式准备要保存的数据
    let data = {};
    
    if (mode === 'add-version') {
      // 如果是添加新版本，提供内容、描述和版本号
      data = {
        content: content.trim(),
        description: description.trim() || `版本 ${prompt.versions.length + 1}`,
        versionNumber: versionNumber.trim() || `${prompt.versions.length + 1}.0`,
        resultImages: resultImages // 添加结果图片
      };
    } else if (mode === 'create') {
      // 如果是创建新提示词，提供完整的提示词数据
      data = {
        title: title.trim(),
        introduction: introduction.trim(),
        content: content.trim(),
        tags: selectedTagIds,
        folderId: folderId,
        versionNumber: versionNumber.trim() || '1.0',
        resultImages: resultImages // 添加结果图片
      };
      console.log('创建新提示词数据:', data);
    } else {
      // 如果是编辑现有提示词
      data = {
        id: prompt.id,
        title: title.trim(),
        introduction: introduction.trim(),
        tags: selectedTagIds,
        folderId: folderId,
        content: content.trim(),
        versionNumber: versionNumber.trim() || prompt.versions[prompt.currentVersionIndex].versionNumber,
        resultImages: resultImages // 添加结果图片
      };
    }
    
    // 调用保存函数
    onSave(data);
  };

  // 记录提示词使用情况
  useEffect(() => {
    // 仅在编辑模式和添加版本模式下记录使用情况
    if ((mode === 'edit' || mode === 'add-version') && prompt?.id) {
      // 异步记录使用情况，不阻塞UI
      DB.recordPromptUsage(prompt.id).catch(error => {
        console.error('记录提示词使用失败:', error);
      });
    }
  }, [mode, prompt]);

  return (
    <Card 
      variant="outlined" 
      className="card-modern shadow-medium rounded-2xl border-subtle overflow-hidden"
      styles={{ body: { padding: '24px' } }}
    >
      {/* 顶部操作栏 */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-[var(--apple-divider)]">
        <Title level={3} className="text-[var(--apple-text-primary)] m-0 font-semibold">{getTitle()}</Title>
        <Space size={12}>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSubmit}
            className="btn-modern-primary"
          >
            保存
          </Button>
          <Button 
            icon={<CloseOutlined />} 
            onClick={onCancel}
            className="btn-modern-secondary"
          >
            取消
          </Button>
        </Space>
      </div>
      
      <Form layout="vertical" className="prompt-editor-form">
        {/* 基本信息区域 */}
        {(mode === 'create' || mode === 'edit') && (
          <div className="basic-info-section mb-6">
            {/* 标题输入 */}
            <Form.Item 
              label={<span className="text-[var(--apple-text-primary)] font-medium">标题</span>} 
              required={true}
              className="mb-4"
            >
              <Input
                placeholder="输入提示词标题..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-modern"
                size="large"
              />
            </Form.Item>
            
            {/* 简介输入 */}
            <Form.Item 
              label={<span className="text-[var(--apple-text-primary)] font-medium">简介</span>}
              className="mb-4"
            >
              <TextArea
                placeholder="输入提示词简介..."
                value={introduction}
                onChange={(e) => setIntroduction(e.target.value)}
                className="input-modern"
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
              <div className="text-xs text-gray-500 mt-1">
                <InfoCircleOutlined className="mr-1" />
                自动识别网址并转换为可点击链接
              </div>
            </Form.Item>
            
            {/* 组织信息（文件夹&标签）*/}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item 
                  label={
                    <div className="flex items-center">
                      <FolderOutlined className="mr-1 text-[var(--apple-accent)]" />
                      <span className="text-[var(--apple-text-primary)] font-medium">文件夹</span>
                    </div>
                  }
                  className="mb-4"
                >
                  <Select
                    placeholder="选择文件夹"
                    value={folderId === null ? '' : folderId}
                    onChange={setFolderId}
                    className="w-full"
                    dropdownStyle={{ borderRadius: '12px', padding: '8px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
                    optionLabelProp="label"
                  >
                    <Option value="" label="未分类">
                      <div className="flex items-center">
                        <FolderOutlined className="mr-2 text-[var(--apple-text-tertiary)]" />
                        <span>未分类</span>
                      </div>
                    </Option>
                    {folders.map(folder => (
                      <Option key={folder.id} value={folder.id} label={folder.name}>
                        <div className="flex items-center">
                          <FolderOutlined className="mr-2 text-[var(--apple-accent)]" />
                          <span>{folder.name}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  label={
                    <div className="flex items-center">
                      <TagOutlined className="mr-1 text-[var(--apple-accent)]" />
                      <span className="text-[var(--apple-text-primary)] font-medium">版本号</span>
                    </div>
                  }
                  className="mb-4"
                >
                  <Input
                    placeholder="输入版本号..."
                    value={versionNumber}
                    onChange={(e) => setVersionNumber(e.target.value)}
                    className="input-modern"
                    prefix={<BranchesOutlined className="text-[var(--apple-text-tertiary)]" />}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            {/* 标签输入和展示 */}
            <Form.Item
              label={
                <div className="flex items-center">
                  <TagOutlined className="mr-1 text-[var(--apple-accent)]" />
                  <span className="text-[var(--apple-text-primary)] font-medium">标签</span>
                </div>
              }
              className="mb-4"
            >
              <div className="flex items-center mb-4">
                <AutoComplete
                  placeholder="输入标签名称后按回车添加..."
                  value={tagInput}
                  onChange={(value) => setTagInput(value)}
                  onSelect={handleTagSelect}
                  onKeyDown={handleTagKeyDown}
                  options={tagSuggestions}
                  className="mr-2 flex-1"
                  style={{ width: '100%' }}
                  dropdownClassName="tag-dropdown"
                  notFoundContent="无匹配标签，回车创建新标签"
                >
                  <Input 
                    className="input-modern"
                  />
                </AutoComplete>
                <Button 
                  onClick={() => addTag()}
                  icon={<PlusOutlined />}
                  disabled={!tagInput.trim()}
                  className="btn-modern-secondary"
                >
                  添加
                </Button>
              </div>
              
              <div className="tags-container flex flex-wrap gap-2 mt-4 pt-2">
                {selectedTagIds.length > 0 ? (
                  selectedTagIds.map(tagId => {
                    const tag = allTags.find(t => t.id === tagId);
                    return (
                      <Tag
                        key={tagId}
                        closable
                        onClose={() => removeTag(tagId)}
                        className="tag-modern"
                      >
                        {tag ? tag.name : '未知标签'}
                      </Tag>
                    );
                  })
                ) : (
                  <div className="text-[var(--apple-text-tertiary)] text-sm">暂无标签</div>
                )}
              </div>
            </Form.Item>
          </div>
        )}
        
        {/* 版本描述 - 仅在添加新版本时显示 */}
        {mode === 'add-version' && (
          <Form.Item 
            label={<span className="text-[var(--apple-text-primary)] font-medium">版本描述</span>}
            className="mb-4"
          >
            <TextArea
              placeholder="描述这个版本的变更..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-modern"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        )}
        
        {/* 提示词内容编辑区 */}
        <Form.Item 
          label={
            <div className="flex items-center justify-between">
              <span className="text-[var(--apple-text-primary)] font-medium">提示词内容</span>
              <Tooltip title="支持多行文本，用于输入完整的提示词内容">
                <InfoCircleOutlined className="text-[var(--apple-text-tertiary)]" />
              </Tooltip>
            </div>
          }
          required={true}
          className="mb-4"
        >
          <TextArea
            placeholder="输入提示词内容..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="input-modern scrollbar-elegant"
            style={{ 
              fontFamily: 'monospace', 
              fontSize: '14px', 
              lineHeight: '1.6', 
              padding: '16px',
              borderRadius: '12px',
              minHeight: '300px' 
            }}
          />
        </Form.Item>
        
        {/* 运行结果图片上传区域 */}
        <Form.Item
          label={
            <div className="flex items-center">
              <PictureOutlined className="mr-1 text-[var(--apple-accent)]" />
              <span className="text-[var(--apple-text-primary)] font-medium">运行结果图片</span>
              <Tooltip title="上传提示词运行后的结果图片，帮助记录和分享效果">
                <InfoCircleOutlined className="ml-1 text-[var(--apple-text-tertiary)]" />
              </Tooltip>
            </div>
          }
          className="mb-4"
        >
          <div className="mb-2">
            <Typography.Text type="secondary">
              <InfoCircleOutlined className="mr-1" />
              上传提示词运行后的结果图片，最多3张，每张不超过5MB
            </Typography.Text>
          </div>
          <ImageUploader
            value={resultImages}
            onChange={setResultImages}
            maxCount={3}
          />
        </Form.Item>
      </Form>
      
      {/* 底部按钮区域 - 在较窄屏幕上使用 */}
      <div className="mt-8 flex justify-end sm:hidden">
        <Space size={12}>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSubmit}
            className="btn-modern-primary"
            size="large"
            block
          >
            保存
          </Button>
          <Button 
            icon={<CloseOutlined />} 
            onClick={onCancel}
            className="btn-modern-secondary"
            size="large"
            block
          >
            取消
          </Button>
        </Space>
      </div>
    </Card>
  );
}

export default PromptEditor;
